/** @jsxImportSource @emotion/react */
import { useApolloClient } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { bulkUpdatePracticeTest, bulkUpdatePracticeTestVariables } from '__generatedTypes__/bulkUpdatePracticeTest'
import {
  findAllPracticeTest,
  findAllPracticeTest_findAllPracticeTest_items as PracticeTest,
  findAllPracticeTestVariables,
} from '__generatedTypes__/findAllPracticeTest'
import { OrderDirection, PracticeTestTypeEnum } from '__generatedTypes__/globalTypes'
import { updatePracticeTest, updatePracticeTestVariables } from '__generatedTypes__/updatePracticeTest'
import { SubjectTabs } from 'components/SubjectsTabs'
import { ClassroomTabLiveSwitcher } from 'components/ClassroomTabLiveSwitcher'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { BULK_UPDATE_PRACTICE_TEST, GET_LIST_PRACTICE_TEST, UPDATE_PRACTICE_TEST } from 'queries/practiceTest'
import { useLink } from 'routes/cmsRoutes'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { getPTConfig } from '../consts'
import * as classes from './styles'
import { PracticeTestsTable } from './components/PracticeTestsTable'

export const List: FC<{ type?: PracticeTestTypeEnum }> = ({ type = PracticeTestTypeEnum.PracticeTest }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const subjectId = searchParams.get('subjectId')
  const { navigate, getLink } = useLink()
  const { query, mutate } = useApolloClient()
  const [practiceTests, setPracticeTests] = useState<PracticeTest[]>([])
  const [submittingPracticeTestId, setSubmittingPracticeTestId] = useState<string | undefined>()
  const [isLoaded, setIsLoaded] = useState(false)
  const { subjects, selectedSubject, selectSubject, selectedPlatformSlug } = usePlatformsAndSubjects({
    initialSelectedSubjectId: subjectId ?? undefined,
  })

  const config = useMemo(
    () => getPTConfig(type, selectedPlatformSlug as PlatformSlug, selectedSubject, false),
    [type, selectedSubject, selectedPlatformSlug]
  )

  const loadPracticeTest = useCallback(async () => {
    if (!selectedSubject || !config) {
      return
    }

    setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), subjectId: selectedSubject.id }))
    const { data } = await query<findAllPracticeTest, findAllPracticeTestVariables>({
      query: GET_LIST_PRACTICE_TEST,
      variables: {
        params: {
          take: TAKE_ALL_ITEMS_COUNT,
          where: {
            subject: {
              id: selectedSubject.id,
            },
            type: config.type,
          },
          order: {
            order: OrderDirection.ASC,
          },
        },
      },
      fetchPolicy: 'network-only',
    })
    setPracticeTests(data.findAllPracticeTest.items ?? [])
    setIsLoaded(true)
  }, [selectedSubject, config])

  useEffect(() => {
    loadPracticeTest()
  }, [loadPracticeTest])

  const handleIsLiveChange = useCallback(
    async (practiceTest: PracticeTest, isLiveUpd: boolean) => {
      setSubmittingPracticeTestId(practiceTest.id)
      setPracticeTests((prev) =>
        prev.map((currentPracticeTest) => {
          if (practiceTest.id !== currentPracticeTest.id) {
            return currentPracticeTest
          }

          return {
            ...currentPracticeTest,
            isLive: isLiveUpd,
          }
        })
      )

      try {
        await mutate<updatePracticeTest, updatePracticeTestVariables>({
          mutation: UPDATE_PRACTICE_TEST,
          variables: {
            params: {
              id: practiceTest.id,
              isLive: isLiveUpd,
            },
          },
        })
        toast.success(`${config?.titleSingle} updated successfully`)
      } catch (e) {
        toast.error(`Failed to update ${config?.titleSingle}`)
        await loadPracticeTest()
      }

      setSubmittingPracticeTestId(undefined)
    },
    [loadPracticeTest, config]
  )

  const handleAddClick = useCallback(() => {
    if (!selectedSubject || !config) {
      return
    }

    navigate(config.createLink, undefined, {
      searchParams: new URLSearchParams({
        subjectId: selectedSubject.id,
        defaultOrder: String(practiceTests.length),
        ...(config.createSearchParams ?? {}),
      }),
    })
  }, [selectedSubject, practiceTests.length, config])

  useEffect(() => {
    if (practiceTests.length > 0) {
      mutate<bulkUpdatePracticeTest, bulkUpdatePracticeTestVariables>({
        mutation: BULK_UPDATE_PRACTICE_TEST,
        variables: {
          params: practiceTests.map(({ id }, order) => ({ id, order })),
        },
      })
    }
  }, [practiceTests.length])

  if (!isLoaded) {
    return <PageLoader />
  }

  if (!config) {
    return null
  }

  return (
    <div css={classes.wrapper}>
      <Typography variant="title3" css={classes.title}>
        {config?.titleMultiple}
      </Typography>
      <SubjectTabs subjects={subjects} selectedSubject={selectedSubject} selectSubject={selectSubject} />
      <div css={classes.tableHeader}>
        <Typography variant="body1" css={classes.tableTitle}>
          {config?.titleSingle}
        </Typography>
        {!!config.classroomTab && (
          <ClassroomTabLiveSwitcher selectedSubject={selectedSubject} tabEnum={config.classroomTab} />
        )}
      </div>
      <PracticeTestsTable
        title={config.titleSingle}
        practiceTests={practiceTests}
        submittingPracticeTestId={submittingPracticeTestId}
        onAddClick={handleAddClick}
        onIsLiveChange={handleIsLiveChange}
        getEditLink={(id) => getLink(config.editLink, { practiceTestId: id })}
        disableTableButtons={!selectedSubject}
        alwaysDisplayLiveSwitch={config.alwaysDisplayLiveSwitch}
      />
    </div>
  )
}
